'use client';
import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { SetContextLink } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import useRateLimitStore from '@/publicSearch/store/useRateLimitStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import useAnonymousSearchLimitStore, {
  ANONYMOUS_RATE_LIMITED_OPERATIONS,
} from '@/publicSearch/store/useAnonymousSearchLimitStore';

export interface CreateApolloClientOptions {
  token: string;
}

const CIRCUIT_OPEN_PATTERN = /circuit is now open/i;
const CIRCUIT_OPEN_COOLDOWN_MS = 30_000;
let circuitOpenUntil = 0;
let lastCircuitWarningAt = 0;

const isCircuitOpen = () => Date.now() < circuitOpenUntil;

const openCircuit = (message?: string) => {
  circuitOpenUntil = Date.now() + CIRCUIT_OPEN_COOLDOWN_MS;

  if (Date.now() - lastCircuitWarningAt > 5_000) {
    lastCircuitWarningAt = Date.now();
    const remainingSeconds = Math.ceil(
      Math.max(circuitOpenUntil - Date.now(), 0) / 1000,
    );
    console.warn(
      `[GraphQL] Circuit open. Pausing requests for ${remainingSeconds}s.`,
      message,
    );
  }
};

const AUTH_NOT_AUTHENTICATED = 'AUTH_NOT_AUTHENTICATED';

/** Empty paged payloads when the client-side guest search cap blocks the request. */
function buildGuestSearchLimitResponse(operationName: string) {
  switch (operationName) {
    case 'GetAnimalEbvs':
      return {
        data: {
          animalEbvs: {
            __typename: 'PagedResultOfAnimalEbvDto',
            items: [],
            totalCount: 0,
          },
        },
      };
    case 'GetMembers':
      return {
        data: {
          members: {
            __typename: 'PagedResultOfMemberDto',
            firstItemNumber: 0,
            hasNextPage: false,
            hasPreviousPage: false,
            lastItemNumber: 0,
            pageNumber: 1,
            pageSize: 0,
            totalCount: 0,
            totalPages: 0,
            items: [],
          },
        },
      };
    default:
      return { data: {} };
  }
}

export const createApolloClient = (
  options: CreateApolloClientOptions | string,
): ApolloClient | null => {
  const { token } = typeof options === 'string' ? { token: options } : options;

  const graphqlProxyUri = '/api/publicSearch/graphql';

  const httpLink = new HttpLink({
    uri: graphqlProxyUri,
  });

  const loggingLink = new ApolloLink((operation, forward) => {
    if (process.env.NODE_ENV !== 'development') return forward(operation);

    return forward(operation).pipe(
      map((response) => {
        const r = response as Record<string, any>;
        console.log(`[GraphQL Response] ${operation.operationName}:`, {
          hasData: !!r.data,
          hasErrors: !!r.errors,
          errors: r.errors,
          dataKeys: r.data ? Object.keys(r.data) : [],
        });

        if (operation.operationName === 'GetAnimalEbvs' && r.data?.animalEbvs) {
          console.log('[GetAnimalEbvs Response Details]:', {
            itemsCount: r.data.animalEbvs.items?.length ?? 0,
            totalCount: r.data.animalEbvs.totalCount,
            firstItem: r.data.animalEbvs.items?.[0],
          });
        }

        return response;
      }),
    );
  });

  const anonymousSearchLimitLink = new ApolloLink((operation, forward) => {
    const operationName = operation.operationName;
    if (
      !operationName ||
      !ANONYMOUS_RATE_LIMITED_OPERATIONS.has(operationName)
    ) {
      return forward(operation);
    }

    // useQuery can invoke this link synchronously during render. Defer store
    // reads/writes so we don't update SocietyLayout subscribers mid-render.
    return new Observable((observer) => {
      queueMicrotask(() => {
        const limitStore = useAnonymousSearchLimitStore.getState();
        if (limitStore.isAuthenticated) {
          forward(operation).subscribe(observer);
          return;
        }

        limitStore.syncFromStorage();
        const { isLimited, tryRecordSearch } =
          useAnonymousSearchLimitStore.getState();

        if (isLimited || !tryRecordSearch()) {
          observer.next(buildGuestSearchLimitResponse(operationName));
          observer.complete();
          return;
        }

        forward(operation).subscribe(observer);
      });
    });
  });

  const circuitBreakerLink = new ApolloLink((operation, forward) => {
    if (isCircuitOpen()) {
      const remainingSeconds = Math.ceil(
        Math.max(circuitOpenUntil - Date.now(), 0) / 1000,
      );
      return new Observable((observer) => {
        observer.error(
          new Error(`GraphQL circuit open. Retry in ${remainingSeconds}s.`),
        );
      });
    }
    return forward(operation);
  });

  const abortableLink = new ApolloLink((operation, forward) => {
    const controller = new AbortController();
    const meta: PendingControllerMeta = { explicitAbort: false };
    pendingRequestControllers.set(controller, meta);

    operation.setContext(({ fetchOptions = {} }) => ({
      fetchOptions: {
        ...fetchOptions,
        signal: controller.signal,
      },
    }));

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (err) => {
          pendingRequestControllers.delete(controller);
          const isAbortError =
            !!err &&
            (err.name === 'AbortError' ||
              err.message?.includes('signal is aborted') ||
              err.message?.includes('aborted'));
          if (isAbortError) {
            observer.complete();
            return;
          }
          observer.error(err);
        },
        complete: () => {
          pendingRequestControllers.delete(controller);
          observer.complete();
        },
      });

      return () => {
        pendingRequestControllers.delete(controller);
        // Only abort the underlying fetch when something explicitly asked us
        // to (abortAllPendingGraphQL). React StrictMode dev runs every
        // useEffect through setup → cleanup → setup, and Apollo's
        // useLazyQuery unsubscribes from this Observable during the cleanup
        // half. If we abort here, the in-flight GetAnimalEbvs / etc. fetch
        // for a freshly-mounted page is killed, the abortable link converts
        // the AbortError into observer.complete() with no data, and the
        // ObservableQuery is left in a "completed without value" state where
        // the second setup's getX() call won't reissue a network request —
        // the grid is then stuck on loading=true forever with no POST.
        //
        // Letting the request continue when a consumer goes away is wasteful
        // (the response is dropped), but it's safe: the cache write happens
        // regardless and will be available to the next mount, and explicit
        // cancellation paths (society switch, clear filter, dialog close)
        // still kill in-flight work via abortAllPendingGraphQL.
        if (meta.explicitAbort) {
          try {
            controller.abort('Cancelled by user');
          } catch {}
        }
        subscription.unsubscribe();
      };
    });
  });

  const authLink = new SetContextLink(({ headers }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  }));

  const societyContextLink = new SetContextLink(({ headers }) => {
    // Apollo only talks to GraphQL backends (breedlogic-sb-graphql-api / i6),
    // both of which scope requests via X-Society-Id only. The registry/core
    // backend reads X-Organisation-Id but is reached via createApiClients,
    // not Apollo, so that header is set there independently.
    const { society } = usePublicSearchContext.getState();
    const societyId = society.societyId ?? society.orgId;
    const extra: Record<string, string> = {};
    if (societyId != null && societyId !== '')
      extra['X-Society-Id'] = String(societyId);
    return {
      headers: {
        ...extra,
        ...headers,
      },
    };
  });

  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path, extensions }) => {
        if (CIRCUIT_OPEN_PATTERN.test(message)) {
          openCircuit(message);
        }
        const code = (extensions as { code?: string })?.code;
        const pathStr = Array.isArray(path)
          ? path.join('.')
          : String(path ?? '');
        const isOptionalAuthError =
          code === AUTH_NOT_AUTHENTICATED &&
          (pathStr.includes('i4LinkStatus') || pathStr.includes('i4Links'));
        const isUserFacingRedirectError =
          (code === 'TOKEN_DECRYPT_FAILED' || code === 'REDIRECT_URL_ERROR') &&
          pathStr.includes('i4RedirectUrl');
        if (isOptionalAuthError) {
          console.warn(
            `[GraphQL] Auth failed for ${pathStr} — user context not recognized by GraphQL API. ` +
              `Check HMAC key alignment between frontend (HMAC_USER_CONTEXT_KEY) and GraphQL API (UserContextSettings:HmacKey).`,
            { message, extensions },
          );
        } else if (isUserFacingRedirectError) {
          console.warn(`[GraphQL] i4RedirectUrl: ${message}`);
        } else {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
            extensions,
          );
        }
      });
    } else {
      const networkError = error;
      console.error(`[Network error]: ${networkError.message}`, networkError);

      if (
        'statusCode' in networkError &&
        (networkError as any).statusCode === 429
      ) {
        try {
          const body =
            'bodyText' in networkError
              ? JSON.parse((networkError as any).bodyText)
              : null;

          useRateLimitStore.getState().setRateLimited({
            resetsAt:
              body?.resetAt ??
              new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            limit: body?.limit ?? 25,
            message:
              body?.message ??
              'You have reached the search limit. Please sign in for unlimited access.',
          });
        } catch {
          useRateLimitStore.getState().setRateLimited({
            resetsAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            limit: 25,
            message:
              'You have reached the search limit. Please sign in for unlimited access.',
          });
        }
        return;
      }

      if (
        'statusCode' in networkError &&
        (networkError as any).statusCode === 401
      ) {
        let message = networkError.message;
        let code: string | undefined;
        if (
          'bodyText' in networkError &&
          typeof (networkError as any).bodyText === 'string'
        ) {
          try {
            const body = JSON.parse(
              (networkError as any).bodyText as string,
            ) as { error?: string; code?: string };
            if (body?.error) message = body.error;
            if (body?.code) code = body.code;
          } catch {
            // keep default message
          }
        }
        const err = new Error(message) as Error & { code?: string };
        if (code) err.code = code;
        return new Observable((observer) => observer.error(err));
      }
    }

    return;
  });

  return new ApolloClient({
    link: ApolloLink.from([
      errorLink,
      anonymousSearchLimitLink,
      authLink,
      societyContextLink,
      circuitBreakerLink,
      loggingLink,
      abortableLink,
      httpLink,
    ]),

    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            animals: {
              merge: false,
            },
            animalEbvs: {
              merge: false,
            },
            societies: {
              merge: false,
            },
            sales: {
              merge: false,
            },
            members: {
              merge: false,
            },
          },
        },
      },
      possibleTypes: {},
    }),

    defaultOptions: {
      watchQuery: {
        fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      },
      query: {
        fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      },
    },
  });
};

type PendingControllerMeta = { explicitAbort: boolean };

const pendingRequestControllers = new Map<
  AbortController,
  PendingControllerMeta
>();

export const abortAllPendingGraphQL = () => {
  for (const [controller, meta] of pendingRequestControllers) {
    meta.explicitAbort = true;
    try {
      controller.abort();
    } catch {}
  }
  pendingRequestControllers.clear();
};
